import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import withLifecycle from '@hocs/with-lifecycle';
import { AsYouType } from 'libphonenumber-js/min';

import {
  Frame,
  SpacerVertical,
  Typography,
  AlertBannerSuccess,
  ButtonPrimary,
  ScrollView,
} from '@klarna/bubble-ui';
import { translator } from 'mage-i18n';
import { components as clientComponents } from 'mage-client';
import { VerticalRhythm } from 'mage-components';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';
import { phoneNumber as phoneNumberValidator } from '@klarna/mf-validations';

import { analytics, FieldsetForObject, formUtils, Input } from 'moet-core';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import { CY_PHONE_COLLECTION_BLOCK_IDS, T_PREFIX as tPrefix } from './constants';
import { getMerchant, hasPhoneNumber, merchantRegion } from '@/selectors/merchant';
import { CAPS } from '@/constants';
import { validators } from '@/flow/validation';

class PhoneCollectionBlockComponent extends React.Component {
  constructor(props) {
    super(props);

    const { t } = this.props;
    const fields = {
      phone: {
        component: Input,
        type: 'tel',
        size: 1,
        label: t(`${tPrefix}.fields.phone.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.phone.errors.required`),
          },
          {
            callback: phoneNumberValidator.validate,
            errorMessage: t(`${tPrefix}.fields.phone.errors.invalid`),
          },
        ],
        isInitialized: false,
        isValid: false,
        value: '+1',
        extraProps: {
          ...generateCyElemId(CY_PHONE_COLLECTION_BLOCK_IDS.PHONE, window),
          tooltipText: t(`${tPrefix}.fields.phone.tooltip`),
        },
      },
    };

    this.state = { fields, isLoading: false };

    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  onBlur(key) {
    formUtils.onBlurFields(key, this.state, fields => this.setState({ fields }));
  }

  onChange(key, value) {
    const formatedValue = key === 'phone' ? new AsYouType().input(value) : value;
    formUtils.onChangeFields(key, formatedValue, this.state, fields => this.setState({ fields }));
  }

  onSubmit() {
    const { savePhoneNumber, merchant, merchantRegion } = this.props;
    const { phone } = formUtils.getMappedValues(this.state.fields);
    const setIsLoading = isLoading => this.setState({ isLoading });

    formUtils.callToActionOnNextWrapper(this.state.fields, () => {
      setIsLoading(true);
      const phoneNumber = String(phone).replace(/\s/g, '');

      analytics.logEvent(ANALYTICS_EVENTS.phoneNumberForSupport);
      return savePhoneNumber({
        params: {
          mid: merchant.id,
        },
        body: { phoneNumber },
        region: merchantRegion,
      }).then(() => setIsLoading(false));
    });
  }

  render() {
    const { t, hasPhoneNumber } = this.props;
    const { isLoading, fields } = this.state;

    return (
      <div style={{ border: 'solid 0.5px gray' }}>
        <Frame paddingToken="space.400">
          <Typography textToken="text-style/headings/blocks/bold/tertiary">
            {t(`${tPrefix}.title`)}
          </Typography>
          <VerticalRhythm spacing={4} />
          <Typography textToken="text-style/text/super-paragraphs/body/regular">
            {t(`${tPrefix}.description`)}
          </Typography>
          {hasPhoneNumber && (
            <>
              <SpacerVertical small />
              <AlertBannerSuccess content={t(`${tPrefix}.success_banner`)} />
            </>
          )}
          {!hasPhoneNumber && (
            <>
              <FieldsetForObject
                name="phone-collection"
                fields={fields}
                onChange={this.onChange}
                onBlur={this.onBlur}
              />
              <SpacerVertical small />
              <ScrollView style={{ alignItems: 'flex-start' }}>
                <ButtonPrimary size="medium" onClick={this.onSubmit} loading={isLoading}>
                  {t(`${tPrefix}.submit`)}
                </ButtonPrimary>
              </ScrollView>
            </>
          )}
        </Frame>
      </div>
    );
  }
}

PhoneCollectionBlockComponent.propTypes = {
  hasPhoneNumber: PropTypes.bool.isRequired,
  merchant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string,
  }).isRequired,
  merchantRegion: PropTypes.string.isRequired,
  savePhoneNumber: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export const PhoneCollectionBlock = compose(
  clientComponents.withPromiseBasedCapabilities({
    getPhoneNumber: CAPS.V2_MERCHANTS_PHONE_NUMBER_GET,
    savePhoneNumber: CAPS.V2_MERCHANTS_PHONE_NUMBER_POST,
  }),
  connect(state => ({
    t: translator(state),
    merchant: getMerchant(state),
    merchantRegion: merchantRegion(state),
    hasPhoneNumber: hasPhoneNumber(state),
  })),
  withLifecycle({
    onDidMount({ getPhoneNumber, merchant: { id: mid } = {}, merchantRegion }) {
      if (mid) {
        getPhoneNumber({ params: { mid }, region: merchantRegion });
      }
    },
  }),
)(PhoneCollectionBlockComponent);
